import { InlineAlertType, InlineAlertWidget, TabStyles, TextLinkWidget, Tooltip, TooltipPosition } from '@tableau/widgets';
import * as React from 'react';
import { media, style } from 'typestyle';
import { NestedCSSProperties } from 'typestyle/lib/types';
import { Messages } from '../../../codegen/Localize';
import { imgUrl } from '../../../common/utilities/ImgUrl';
import { L10n } from '../../../ts/L10n/L10n';
import { IMetricInfo } from '../../../ts/models/IMetricInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedViewThumbnailAngular } from './ConnectedViewThumbnail';
import { ConnectedMetricDescriptionSection } from './MetricDescriptionSection';
import { BreakPoints, contentVerticalSpace, metadataSectionStyle, metadataSubTitle, metadataText, metadataThumbnail } from './MetricsStyles';
import MetricTagsSection from './MetricTagsSection';

const { Colors } = TabStyles;

export interface MetricMetadataPanelProps {
  metricInfo: IMetricInfo;
  urls: SpacesSiteUrls;
}

const metadataPanelStyle: string = style(
  { display: 'flex', flexDirection: 'column', '-webkit-text-size-adjust': '100%' },
  media({ minWidth: 0, maxWidth: BreakPoints.LARGE }, { marginTop: '40px' }),
  media({ minWidth: BreakPoints.LARGE + 1 }, { width: '290px', marginLeft: '40px', marginTop: '12px' }),
);

const columnContainer: string = style(
  { display: 'flex', flexDirection: 'column', paddingBottom: '24px' },
  media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.LARGE }, { flexDirection: 'row' }),
);

const leftColumnStyle: string = style(
  media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.LARGE }, { flex: 'none', width: '50%', paddingRight: '20px', boxSizing: 'border-box' }),
);

const rightColumnStyle: string = style(
  media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.LARGE }, { flex: 'none', width: '50%', paddingLeft: '20px', boxSizing: 'border-box' }),
);

const connectedViewHorizontalLineStyle: string = style(
  metadataSectionStyle as NestedCSSProperties,
  media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.LARGE }, { display: 'none' })
);

const tableCalculationIconStyle: string = style(
  {
    marginLeft: '4px',
    width: '17px',
    height: '17px',
    backgroundImage: imgUrl('pill_tablecalc.svg'),
    $nest: {
      '&:hover': {
        backgroundImage: imgUrl('pill_tablecalc_active.svg')
      }
    }
  }
);

const metadataTitle: React.CSSProperties = {
  fontWeight: 'normal',
  fontSize: '16px',
  color: Colors!.L9,
};

const hiddenUserText: React.CSSProperties = {
  fontWeight: 'normal',
  fontSize: '12px',
  color: '#BFBFBF',
};

const measureWithCalculation: React.CSSProperties = {
  display: 'inline-flex',
  flexDirection: 'row'
};

const metadataHorizontalLine: React.CSSProperties = {
  border: '0.5px solid #D8D8D8',
};

const flexRow: React.CSSProperties = {
  display: 'flex',
};

const fixedFlexColumn: React.CSSProperties = {
  // Make sure to update the width of this column whenever the
  // text inside changes. The goal is to have about 16px of space
  // between the widest text in this column and the dynamic text column.
  flex: '0 0 105px',
};

const dynamicTextColumn: React.CSSProperties = {
  flex: 1,
};

export class MetricMetadataPanel extends React.PureComponent<MetricMetadataPanelProps> {
  private renderMetadataPanelOwnerSection = () => {
    if (!this.props.metricInfo.owner || !this.props.metricInfo.owner.displayName) {
      return null;
    }

    const renderUser = (ownerName?: string) => {
      if (this.props.metricInfo.owner && this.props.metricInfo.owner.isHiddenInternalUser) {
        return (
          <div style={hiddenUserText}>
            {ownerName}
          </div>
        );
      } else {
        const ownerUrl = this.props.metricInfo.owner && this.props.urls.userUrl(
          this.props.metricInfo.owner.username,
          this.props.metricInfo.owner.domainName
        );
        return (
          <TextLinkWidget
            href={ownerUrl}
            target={'_blank'}
            testId={'metadata-owner'}
            id={'metadata-owner-name'}
            aria-labelledby={'metadata-owner-label metadata-owner-name'}
          >
            {ownerName}
          </TextLinkWidget>
        );
      }
    };

    return (
      <div className={metadataSectionStyle}>
        <div style={metadataSubTitle} tabIndex={-1} id={'metadata-owner-label'}>{Messages.metricsMetadataPanelOwner()}</div>
        {renderUser(this.props.metricInfo.owner.displayName)}
      </div>
    );
  }

  private renderMetadataPanelConnectedViewSection = () => {
    const renderConnectedViewHeader = () => {
      return (
        <div style={{ marginBottom: contentVerticalSpace }} data-tb-test-id={'metadata-connected-view-header'}>
          <div style={metadataSubTitle} tabIndex={-1} id={'metadata-connected-view-label'}>{Messages.metricsMetadataPanelConnectedView()}</div>
          <div style={metadataText}>{Messages.metricsMetadataPanelConnectedViewSubTitle()}</div>
        </div>
      );
    };

    if (!this.props.metricInfo.connectedView || !this.props.metricInfo.connectedView.name) {
      // render error indicator
      return (
        <div className={metadataSectionStyle}>
          {renderConnectedViewHeader()}
          <InlineAlertWidget
            testId="metadata-connected-view-error"
            type={InlineAlertType.Warning}
          >
            {Messages.metricConnectedViewNotAccessible()}
          </InlineAlertWidget>
        </div>
      );
    }

    const connectedViewUrl = this.props.urls.viewUrl(this.props.metricInfo.connectedView.path);
    return (
      <div className={metadataSectionStyle}>
        {renderConnectedViewHeader()}
        <div style={metadataThumbnail} aria-labelledby={'metadata-connected-view-label metadata-connected-view-title'}>
          <ConnectedViewThumbnailAngular
            connectedViewId={this.props.metricInfo.connectedView.viewId}
            connectedViewThumbnailUrl={this.props.metricInfo.connectedView.thumbnailUrl}
            connectedViewUrl={connectedViewUrl}
          />
        </div>
        <div>
          <TextLinkWidget
            href={connectedViewUrl}
            target={'_blank'}
            testId={'metadata-connected-view'}
            id={'metadata-connected-view-title'}
            aria-labelledby={'metadata-connected-view-label metadata-connected-view-title'}
          >
            {this.props.metricInfo.connectedView.name}
          </TextLinkWidget>
        </div>
      </div>
    );
  }

  private renderMetadataPanelDefinitionSection = () => {
    const schemaExists = this.props.metricInfo.schema && (this.props.metricInfo.schema.measure.displayName || this.props.metricInfo.schema.timestamp);
    const connectedViewNameExists = this.props.metricInfo.connectedView && this.props.metricInfo.connectedView.name;
    if (!connectedViewNameExists || !schemaExists) {
      return null;
    }

    const renderDimensionTitle = () => {
      if (!this.props.metricInfo.schema.timestamp) {
        return null;
      }
      return (
        <div style={metadataText}>
          {Messages.metricsMetadataPanelDateDimension()}
        </div>
      );
    };

    const renderDimensionValue = () => {
      if (!this.props.metricInfo.schema.timestamp) {
        return null;
      }
      const metricInfoTimestamp = this.props.metricInfo.schema.timestamp;
      const schemaTimestamp = metricInfoTimestamp.isSnapshotTimestamp ?
        Messages.metricsMetadataPanelDateDimensionNone() : metricInfoTimestamp.displayName;

      return (
        <div style={metadataText} data-tb-test-id={'metadata-dimension-value'}>
          {schemaTimestamp}
        </div>
      );
    };

    const renderMeasureValue = () => {
      const schema = this.props.metricInfo.schema;
      if (schema.measure.tableCalculationInfo) {
        return <div style={measureWithCalculation}>
          <div style={metadataText}>{schema.measure.displayName}</div>
          <Tooltip
            position={TooltipPosition.Bottom}
            getContent={() => <span>{schema.measure.tableCalculationInfo.calculationDescription}</span>}
          >
            <div className={tableCalculationIconStyle} />
          </Tooltip>
        </div>;
      }
      return schema.measure.displayName;
    };

    return (
      <div className={metadataSectionStyle}>
        <div style={metadataSubTitle}>{Messages.metricsMetadataPanelDefinition()}</div>
        <div style={flexRow}>
          <div style={fixedFlexColumn}>
            <div style={metadataText}>
              {Messages.metricsMetadataPanelMeasure()}
            </div>
          </div>
          <div style={dynamicTextColumn}>
            <div style={metadataText} data-tb-test-id={'metadata-measure-value'}>
              {renderMeasureValue()}
            </div>
          </div>
        </div>
        <div style={{ ...flexRow, marginTop: contentVerticalSpace }}>
          <div style={fixedFlexColumn}>
            <div style={metadataText}>
              {renderDimensionTitle()}
            </div>
          </div>
          <div style={dynamicTextColumn}>
            <div style={metadataText}>
              {renderDimensionValue()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderMetadataPanelProjectSection = () => {
    if (!this.props.metricInfo.project || !this.props.metricInfo.project.name) {
      return null;
    }

    const projectUrl = this.props.urls.projectUrl(this.props.metricInfo.project.id);
    return (
      <div className={metadataSectionStyle}>
        <div style={metadataSubTitle} tabIndex={-1} id={'metadata-project-label'}>{Messages.metricsMetadataPanelProject()}</div>
        <div>
          <TextLinkWidget
            href={projectUrl}
            target={'_blank'}
            testId={'metadata-project'}
            id={'metadata-project-name'}
            aria-labelledby={'metadata-project-label metadata-project-name'}
          >
            {this.props.metricInfo.project.name}
          </TextLinkWidget>
        </div>
      </div>
    );
  }

  private renderMetadataPanelModifiedSection = () => {
    return (
      <div className={metadataSectionStyle}>
        <div style={metadataSubTitle}>{Messages.metricsMetadataPanelModified()}</div>
        <div style={metadataText} data-tb-test-id={'metadata-modified-date'}>
          {L10n.localizedDateTime(this.props.metricInfo.updatedAt)}
        </div>
      </div>
    );
  }

  public render(): JSX.Element {
    return (
      <div className={metadataPanelStyle}>
        <div>
          <div className={metadataSectionStyle}>
            <div role="heading" aria-level={2} style={metadataTitle}>
              {Messages.metricsMetadataPanelTitle()}
            </div>
          </div>
        </div>
        <div className={columnContainer}>
          <div className={leftColumnStyle}>
            {this.renderMetadataPanelOwnerSection()}
            <ConnectedMetricDescriptionSection metricInfo={this.props.metricInfo} />
            <div className={metadataSectionStyle}>
              <hr style={metadataHorizontalLine} />
            </div>
            {this.renderMetadataPanelConnectedViewSection()}
            {this.renderMetadataPanelDefinitionSection()}
            <div className={connectedViewHorizontalLineStyle}>
              <hr style={metadataHorizontalLine} />
            </div>
          </div>
          <div className={rightColumnStyle}>
            {this.renderMetadataPanelProjectSection()}
            {this.renderMetadataPanelModifiedSection()}
            <MetricTagsSection metricInfo={this.props.metricInfo} />
          </div>
        </div>
      </div>
    );
  }
}
